import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../stores/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import PlanBadge from './PlanBadge';
import { trackLogout } from '../../services/analytics';
import { Activity, List, TrendingUp, Brain, Layers, BookMarked, Sparkles, Menu, X, User, CreditCard, ArrowUpCircle } from 'lucide-react';

const Navbar = () => {
  const { logout, loginTimestampRef } = useAuth();
  const { isFreeTier } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      const session_duration_minutes = loginTimestampRef?.current
        ? Math.round((Date.now() - loginTimestampRef.current) / 60000)
        : undefined;
      trackLogout({ session_duration_minutes });
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const navigation = [
    { name: 'Pulse',     tagline: 'Your trading day, at a glance',        path: '/dashboard',   icon: Activity   },
    { name: 'Trades',    tagline: 'Review and manage your trades',         path: '/trades',      icon: List       },
    { name: 'Edge',      tagline: 'Quantify your advantage',               path: '/performance', icon: TrendingUp },
    { name: 'Cortex',    tagline: 'Know why you trade the way you do',     path: '/psychology',  icon: Brain      },
    { name: 'Blueprint', tagline: 'Engineer your trading system',          path: '/playbook',    icon: Layers     },
    { name: 'Chronicle', tagline: 'The record that makes you better',      path: '/journal-ai',  icon: BookMarked },
    { name: 'Sage',      tagline: 'Ask anything. It knows your history.',  path: '/ai-coach',    icon: Sparkles   },
  ];

  return (
    <div className="fixed top-0 left-0 w-full z-50 bg-card border-b border-border/40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center w-full">
          <div className="flex items-center w-full min-w-0">
            <h1 className="text-xl font-bold bg-gradient-to-r from-[#00D4C8] via-[#00F0E8] to-foreground bg-clip-text text-transparent flex-shrink-0 mr-5">
              CTX
            </h1>
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-0.5 min-w-0">
              {navigation.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <div key={item.name} className="relative group">
                    <Link
                      to={item.path}
                      aria-current={isActive ? 'page' : undefined}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/15 text-primary font-semibold tracking-tight'
                          : 'text-muted-foreground font-medium hover:text-foreground hover:bg-white/[0.06]'
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{item.name}</span>
                    </Link>
                    {/* Tagline tooltip on hover */}
                    <div className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2.5 py-1 rounded-md bg-popover border border-border shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                      <p className="text-xs text-muted-foreground">{item.tagline}</p>
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
          {/* Mobile Hamburger */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Open navigation menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0 ml-2">
            {isFreeTier ? (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors duration-200"
              >
                <ArrowUpCircle className="w-3.5 h-3.5" />
                Upgrade
              </Link>
            ) : (
              <Link to="/billing" className="flex items-center">
                <PlanBadge />
              </Link>
            )}
            <Link
              to="/billing"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                location.pathname === '/billing'
                  ? 'bg-primary/15 text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
              }`}
            >
              <CreditCard className="w-3.5 h-3.5" />
              Billing
            </Link>
            <Link
              to="/profile"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                location.pathname === '/profile'
                  ? 'bg-primary/15 text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              Profile
            </Link>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm font-medium rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-all duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm">
          <div className="absolute top-0 left-0 w-3/4 max-w-xs h-full bg-card border-r border-border shadow-xl p-6 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold bg-gradient-to-r from-[#00D4C8] to-foreground bg-clip-text text-transparent">
                Menu
              </h2>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex flex-col px-3 py-2.5 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-primary/15 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                    }`}
                  >
                    <span className={`inline-flex items-center gap-2.5 text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      {item.name}
                    </span>
                    <span className="text-xs text-muted-foreground mt-0.5 pl-6">{item.tagline}</span>
                  </Link>
                );
              })}
            </nav>
            {isFreeTier && (
              <Link
                to="/pricing"
                onClick={() => setMobileOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <ArrowUpCircle className="w-4 h-4" />
                Upgrade Plan
              </Link>
            )}
            <Link
              to="/billing"
              onClick={() => setMobileOpen(false)}
              className={`mt-1 inline-flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                location.pathname === '/billing'
                  ? 'bg-primary/15 text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
            >
              <CreditCard className="w-4 h-4 mr-2.5" />
              Billing
            </Link>
            <Link
              to="/profile"
              onClick={() => setMobileOpen(false)}
              className={`mt-1 inline-flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                location.pathname === '/profile'
                  ? 'bg-primary/15 text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
            >
              <User className="w-4 h-4 mr-2.5" />
              Profile
            </Link>
            <button
              onClick={handleLogout}
              className="mt-4 px-4 py-2.5 w-full text-sm font-medium rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar; 